import { convertRefStringToProto, getProtoTypeForTypeName, convertToRefString } from 'lib/websocket-notify'
import { UPDATE_SUBSCRIPTION_REFS_TOPIC } from 'lib/ws/election'
import { getWsHub } from 'lib/ws/hub'
import {
    ClientMessage,
    SubscribeMessage,
    RefSubscription,
} from 'pbts/c1/api/websocketnotify/v1/websocketnotify'
import { Any } from 'pbts/google/protobuf/any'
import { OBJECT_SUBSCRIPTIONS, LIST_SUBSCRIPTIONS, MULTIGET_SUBSCRIPTIONS } from 'redux_toolkit/lib/subscriptions'

type SendMessageFn = (message: Uint8Array) => void
export type ReduxStateLike = Record<string, {
    refStrings: string[]
    refType?: string
    watchInserts?: boolean
}>
type AccessTokenProvider = () => Promise<string | undefined>
export type SubscribedRef = {
    refString: string
    refTypeName: string
    watchInserts: boolean
}
export class WebsocketFilterManager {
    private static instance: WebsocketFilterManager | undefined
    private getAccessToken?: AccessTokenProvider
    private sendMessage?: SendMessageFn
    private localSubscriptions: SubscribedRef[] = []
    private subscriptionsByTab = new Map<string, SubscribedRef[]>()
    private pendingSend = false
    private debounceTimer?: number
    private subscriptionChangeTimer?: number
    private disabledWatchInsertsSubscriptions = new Set<string>()

    public static getInstance(): WebsocketFilterManager {
        if (!WebsocketFilterManager.instance) {
            WebsocketFilterManager.instance = new WebsocketFilterManager()
        }
        return WebsocketFilterManager.instance
    }

    public initialize(getAccessToken: AccessTokenProvider): void {
        this.getAccessToken = getAccessToken
    }

    public registerSendFunction(sendFn: SendMessageFn): void {
        this.sendMessage = sendFn

        // If there was a pending send, try it now
        if (this.pendingSend) {
            this.pendingSend = false
            this.debouncedSend()
        }
    }

    public onDisconnect(): void {
        this.sendMessage = undefined
    }

    public onSubscriptionsChanged(priority: 'immediate' | 'normal' = 'normal'): void {
        // debounce
        if (this.subscriptionChangeTimer !== undefined) {
            clearTimeout(this.subscriptionChangeTimer)
        }

        // Context-aware debouncing: immediate for user actions, shorter for normal updates
        const debounceTime = priority === 'immediate' ? 0 : 100

        this.subscriptionChangeTimer = setTimeout(() => {
            const reduxState = this.collectAllSubscriptions()
            this.localSubscriptions = WebsocketFilterManager.extractSubscriptionsFromState(reduxState)

            const hub = getWsHub()
            if (!hub) return

            const tab = hub.getTab()
            if (hub.isLeader()) {
                this.updateSubscriptionRefs(tab.id, this.localSubscriptions)
            } else {
                tab.send({
                    topic: UPDATE_SUBSCRIPTION_REFS_TOPIC,
                    metadata: { fromTabId: hub.getTab().id },
                    payload: this.localSubscriptions,
                })
            }
            this.subscriptionChangeTimer = undefined
        }, debounceTime) as unknown as number
    }

    public onLocalReduxStateChanged(reduxState: ReduxStateLike): void {
        this.localSubscriptions = WebsocketFilterManager.extractSubscriptionsFromState(reduxState)

        const hub = getWsHub()
        if (!hub) return

        const tab = hub.getTab()
        if (hub.isLeader()) {
            this.updateSubscriptionRefs(tab.id, this.localSubscriptions)
        } else {
            tab.send({
                topic: UPDATE_SUBSCRIPTION_REFS_TOPIC,
                metadata: { fromTabId: hub.getTab().id },
                payload: this.localSubscriptions,
            })
        }
    }

    private collectAllSubscriptions(): ReduxStateLike {
        const reduxState: ReduxStateLike = {}

        // Collect from object subscriptions
        for (const [key, value] of Object.entries(OBJECT_SUBSCRIPTIONS)) {
            if (!value.state?.data || !value.tag) {
                continue
            }

            const refString = convertToRefString(value.state.data, value.tag)
            if (refString !== null) {
                reduxState[key] = {
                    refStrings: refString ? [refString] : [],
                    refType: value.tag,
                    watchInserts: false,
                }
            }
        }

        // Collect from list subscriptions
        for (const [key, value] of Object.entries(LIST_SUBSCRIPTIONS)) {
            if (!value.state?.metadata || !value.tag) {
                continue
            }

            // Check if this subscription has watchInserts disabled (e.g., when dirty)
            const isWatchInsertsDisabled = this.disabledWatchInsertsSubscriptions.has(key)

            reduxState[key] = {
                refStrings: Object.keys(value.state.metadata.tags || {}),
                refType: value.tag,
                // Lists watch inserts by default, unless disabled (when dirty)
                watchInserts: !isWatchInsertsDisabled,
            }
        }

        // Collect from multiget subscriptions
        for (const [key, value] of Object.entries(MULTIGET_SUBSCRIPTIONS)) {
            const [tag, refString] = value

            if (!tag || !refString) continue

            // Check if this subscription has watchInserts disabled (same pattern as pagedList)
            const isWatchInsertsDisabled = this.disabledWatchInsertsSubscriptions.has(key)

            reduxState[key] = {
                refStrings: [refString],
                refType: tag,
                // Multiget watches inserts by default, unless explicitly disabled
                watchInserts: !isWatchInsertsDisabled,
            }
        }

        return reduxState
    }

    public updateSubscriptionRefs(tabId: string, refs: SubscribedRef[]): void {
        this.subscriptionsByTab.set(tabId, refs)
        this.debouncedSend()
    }

    public batchUpdateSubscriptionRefs(tabSubscriptions: Map<string, SubscribedRef[]>): void {
        this.subscriptionsByTab.clear()
        tabSubscriptions.forEach((refs, tabId) => this.subscriptionsByTab.set(tabId, refs))
        this.debouncedSend()
    }

    public clearSubscriptionsForTab(tabId: string): void {
        this.subscriptionsByTab.delete(tabId)
        this.debouncedSend()
    }

    public getCurrentSubscriptions(): SubscribedRef[] {
        return [...this.localSubscriptions]
    }

    public setWatchInsertsForSubscription(subscriptionKey: string, enabled: boolean): void {
        if (enabled) {
            this.disabledWatchInsertsSubscriptions.delete(subscriptionKey)
        } else {
            this.disabledWatchInsertsSubscriptions.add(subscriptionKey)
        }

        // Trigger full subscription collection to pick up the watchInserts change
        this.onSubscriptionsChanged('immediate')
    }

    public removeSubscriptionFromTracking(subscriptionKey: string): void {
        this.disabledWatchInsertsSubscriptions.delete(subscriptionKey)
    }

    // ====== PRIVATE METHODS ======

    private debouncedSend(): void {
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer)
        }

        this.debounceTimer = window.setTimeout(() => {
            this.sendGlobalSubscriptionsToWebSocket()
        }, 50)
    }

    private async sendGlobalSubscriptionsToWebSocket(): Promise<void> {
        if (!this.getAccessToken || !this.sendMessage) {
            this.pendingSend = true
            return
        }

        // Deduplicate subscriptions across all tabs
        const uniqueRefs = new Map<string, SubscribedRef>()
        for (const refs of this.subscriptionsByTab.values()) {
            for (const ref of refs) {
                uniqueRefs.set(`${ref.refString}:${ref.refTypeName}`, ref)
            }
        }

        const allSubscriptions = Array.from(uniqueRefs.values())
        const refSubscriptions = WebsocketFilterManager.convertSubscriptionsToRefSubscriptions(allSubscriptions)

        try {
            const token = await this.getAccessToken()
            if (!token) return

            // Re-check after await - websocket may have disconnected during token fetch
            if (!this.sendMessage) {
                this.pendingSend = true
                return
            }

            const message = ClientMessage.toBinary(ClientMessage.create({
                message: {
                    oneofKind: 'subscribe',
                    subscribe: SubscribeMessage.create({
                        accessToken: token,
                        refSubscriptions,
                    }),
                },
            }))

            this.sendMessage(message)
        } catch (err) {
            console.error('Error sending global subscriptions to web socket', err) // eslint-disable-line no-console
        }
    }

    private static extractSubscriptionsFromState(reduxState: ReduxStateLike): SubscribedRef[] {
        const refMap = new Map<string, { refString: string; refTypeName: string; watchInserts: boolean }>()

        for (const rs of Object.values(reduxState)) {
            if (!rs.refType) {
                continue
            }

            const refTypeName = rs.refType
            const watchInserts = rs.watchInserts ?? false

            if (rs.refStrings && rs.refStrings.length > 0) {
                rs.refStrings.forEach((refString) => {
                    const compositeKey = `${refTypeName}::${refString}`
                    refMap.set(compositeKey, { refString, refTypeName, watchInserts })
                })
            } else {
                // Add refTypes with empty refStrings using composite key
                const compositeKey = `${refTypeName}::`
                refMap.set(compositeKey, { refString: '', refTypeName, watchInserts })
            }
        }

        return Array.from(refMap.values())
    }

    private static convertSubscriptionsToRefSubscriptions(refs: SubscribedRef[]): RefSubscription[] {
        const refsByType = new Map<string, SubscribedRef[]>()

        // Group refs by type
        for (const ref of refs) {
            if (!refsByType.has(ref.refTypeName)) {
                refsByType.set(ref.refTypeName, [])
            }
            const typeRefs = refsByType.get(ref.refTypeName)
            if (typeRefs) {
                typeRefs.push(ref)
            }
        }

        // Convert each group to RefSubscription
        return Array.from(refsByType.entries()).map(([refTypeName, groupedRefs]) => {
            const protoRefs = groupedRefs
                .filter(({ refString }) => refString !== '') // Skip empty refStrings
                .map(({ refString }) => {
                    const protoRef = convertRefStringToProto(refString, refTypeName)
                    const ProtoType = getProtoTypeForTypeName(refTypeName)

                    if (!protoRef || !ProtoType) return null

                    return Any.create({
                        typeUrl: `type.googleapis.com/${refTypeName}`,
                        value: ProtoType.toBinary(protoRef),
                    })
                }).filter((ref): ref is Any => ref !== null)

            // Set watchInserts to true if at least one ref in this type has watchInserts: true
            const watchInserts = groupedRefs.some((ref) => ref.watchInserts)

            return RefSubscription.create({
                refType: refTypeName,
                refs: protoRefs,
                watchInserts,
            })
        })
    }
}

export const websocketFilterManager = WebsocketFilterManager.getInstance()
